// "Continue with <provider>" buttons backed by Google Cloud Identity Platform.
// Renders one button per configured provider (lib/gcip GCIP_PROVIDERS); falls back to the
// legacy GIS Google button while VITE_GCIP_* is not configured, so sign-in never breaks
// during the migration.

import { useState } from "react";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";

import { ApiError, type LoginResult } from "@/lib/api";
import {
  GCIP_CONFIGURED,
  GCIP_PROVIDERS,
  SignInCancelled,
  signInWithGcipProvider,
} from "@/lib/gcip";
import { GoogleSignInButton } from "@/components/google-sign-in-button";
import { Button } from "@/components/ui/button";

export function GcipSignIn({
  onToken,
  onDone,
}: {
  onToken: (result: LoginResult) => void;
  onDone: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!GCIP_CONFIGURED) {
    // Migration fallback: legacy Google Identity Services button (apiAuthGoogle exchange).
    return <GoogleSignInButton onToken={onToken} onDone={onDone} />;
  }

  const signIn = (id: string) => {
    setBusyId(id);
    signInWithGcipProvider(id)
      .then((r) => {
        onToken(r);
        onDone();
      })
      .catch((e) => {
        if (e instanceof SignInCancelled) return;
        toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Sign-in failed.");
      })
      .finally(() => setBusyId(null));
  };

  return (
    <div className="flex flex-col gap-2">
      {GCIP_PROVIDERS.map((p) => (
        <Button
          key={p.id}
          type="button"
          variant="outline"
          className="h-12 w-full text-base"
          disabled={busyId !== null}
          onClick={() => signIn(p.id)}
        >
          {busyId === p.id ? <Loader2 className="animate-spin" /> : <LogIn className="size-4" />}
          Continue with {p.label}
        </Button>
      ))}
    </div>
  );
}
